import { FlatKeyNotes, SharpKeyNotes } from 'constants/filters';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Text,
    ToggleButton,
    ToggleButtonGroup,
    useBreakpoint,
    CaretLeftOutlined,
    Group,
    Button,
    Box,
    Flex,
} from '@landr/maestro';
import { Trans } from '@lingui/macro';
import {
    toKeyRootApi,
    KeyRoot,
    KeySign,
    getNormalizedRootValue,
} from 'types/enums';
import { keyboardSorting } from 'utils/sort';
import { useCreatorContext } from 'controllers/business/Creator/Creator.controller';
import { KeyQuality, KeyRootApi } from 'types/generated/graphql';
import { css } from '@landr/styled-system/css';

interface ValueProps<T> {
    value: T;
    setValue: (
        value: T,
    ) => void | React.Dispatch<React.SetStateAction<KeyQuality>>;
}

interface KeysProps {
    value: string;
    setValue: (
        value: string,
    ) => void | React.Dispatch<React.SetStateAction<KeyQuality>>;
    keySign: KeySign;
    normalizedRootValue: KeyRoot | '';
}

const keys = css({
    alignItems: 'center',
    flexWrap: 'wrap',
    '& > *': {
        marginTop: 0,
        marginRight: 'md',
        marginBottom: 'md',
        marginLeft: 0,
    },
    md: {
        width: '100%',
        '& > *': {
            marginBottom: 'xs',
            marginRight: 'xs',
        },
        '& > :nth-child(1)': {
            marginLeft: 'xl',
        },
        '& > :nth-child(3)': {
            marginLeft: '4xl',
        },
        '& > :last-child, & > :nth-child(2)': {
            marginRight: '0',
        },
    },
});

const KeySignToggle = ({
    value,
    setValue,
}: ValueProps<KeySign>): JSX.Element => {
    // TODO: update ButtonGroup to allow onChange to accept a custom type https://mixgenius.atlassian.net/browse/MG-38120
    const handleChange: any = useCallback(
        (_event: React.MouseEvent<HTMLButtonElement>, value: KeySign) => {
            setValue(value);
        },
        [setValue],
    );

    return (
        <ToggleButtonGroup value={value} onChange={handleChange}>
            <ToggleButton value={KeySign.Flat} cursor="pointer">
                <Trans>Flat keys</Trans>
            </ToggleButton>
            <ToggleButton value={KeySign.Sharp} cursor="pointer">
                <Trans>Sharp keys</Trans>
            </ToggleButton>
        </ToggleButtonGroup>
    );
};

const Keys = ({
    keySign,
    setValue,
    normalizedRootValue,
}: KeysProps): JSX.Element => {
    const isMobile = useBreakpoint('md');

    const notesToMap = keySign === KeySign.Flat ? FlatKeyNotes : SharpKeyNotes;
    const notesToMapSorted = isMobile
        ? notesToMap
        : notesToMap.slice(0).sort(keyboardSorting);

    const handleChange: any = useCallback(
        (_event: React.MouseEvent<HTMLButtonElement>, value: any) => {
            setValue(value);
        },
        [setValue],
    );

    return (
        <Flex className={keys}>
            {notesToMapSorted.map((note) => (
                <ToggleButton
                    justifyContent="center"
                    value={note}
                    key={note}
                    isRounded
                    isSelected={note === normalizedRootValue}
                    onChange={handleChange}
                    cursor="pointer"
                >
                    {note}
                </ToggleButton>
            ))}
        </Flex>
    );
};

const KeyQualityToggle = ({
    value,
    setValue,
}: ValueProps<KeyQuality>): JSX.Element => {
    const handleClick: any = useCallback(
        (_event: React.MouseEvent<HTMLButtonElement>, value: KeyQuality) => {
            setValue(value);
        },
        [setValue],
    );

    return (
        <Group gap="sm">
            <Group.Item>
                <ToggleButton
                    isRounded
                    value={KeyQuality.Major}
                    onClick={handleClick}
                    isSelected={value === KeyQuality.Major}
                    cursor="pointer"
                >
                    <Trans>Major</Trans>
                </ToggleButton>
            </Group.Item>
            <Group.Item>
                <ToggleButton
                    isRounded
                    value={KeyQuality.Minor}
                    onClick={handleClick}
                    isSelected={value === KeyQuality.Minor}
                    cursor="pointer"
                >
                    <Trans>Minor</Trans>
                </ToggleButton>
            </Group.Item>
        </Group>
    );
};

export interface KeyControlProps {
    onClick: () => void;
}

export const KeyControl: React.FC<KeyControlProps> = ({
    onClick,
}: KeyControlProps): JSX.Element => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { state, dispatch } = useCreatorContext();

    const numberOfSamplesInCreator = state.samplesInSession.size;

    const [keyRootNote, setKeyRootNote] = useState<KeyRootApi>(state.keyRoot);
    const [keyQuality, setKeyQuality] = useState<KeyQuality>(state.keyQuality);
    const [keySign, setKeySign] = useState(state.keySign);
    const [normalizedRootValue, setNormalizedRootValue] = useState<KeyRoot>(
        KeyRoot.C,
    );

    const setTargetKey = useCallback(
        (root: KeyRootApi, quality: KeyQuality): void => {
            dispatch({
                type: 'changeKey',
                payload: { root, quality, sign: keySign },
            });
        },
        [dispatch, keySign],
    );

    const handleApply = useCallback(() => {
        setTargetKey(keyRootNote, keyQuality);
        onClick();
    }, [
        keyQuality,
        keyRootNote,
        setTargetKey,
        numberOfSamplesInCreator,
        state.instrumentationSessionId,
        onClick,
    ]);

    useEffect(() => {
        setNormalizedRootValue(getNormalizedRootValue(keyRootNote, keySign));
    }, [keyRootNote, keySign]);

    const handleFocus = (): void => {
        containerRef?.current?.focus();
    };

    const handleKeyPress = (event: React.KeyboardEvent): void => {
        if (event.key === 'Enter') {
            handleApply();
        }
    };

    return (
        <Flex
            width={{ base: 'auto', md: '424px' }}
            flexDirection="column"
            justifyContent="space-between"
            outline="none"
            onKeyDown={handleKeyPress}
            ref={containerRef}
            tabIndex={0}
        >
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
            >
                <Button
                    float="left"
                    fontSize="lg"
                    marginTop="lg"
                    display={{ base: 'inherit', md: 'none' }}
                    kind="minimal"
                    icon={CaretLeftOutlined}
                    onClick={onClick}
                >
                    <Trans>Back</Trans>
                </Button>
            </Box>
            <Flex
                height="100%"
                flexDirection="column"
                justifyContent="space-between"
                alignItems="flex-start"
                padding="lg"
                onClick={handleFocus}
            >
                <KeySignToggle value={keySign} setValue={setKeySign} />
                <Box height="sm" width="100%" marginY="sm">
                    <Text textStyle="body" fontWeight="bold">
                        <Trans>Root note</Trans>
                    </Text>
                </Box>
                <Keys
                    setValue={(value: string): void =>
                        setKeyRootNote(toKeyRootApi(value))
                    }
                    value={keyRootNote}
                    keySign={keySign}
                    normalizedRootValue={normalizedRootValue}
                />
                <Box height="sm" width="100%" marginY="sm">
                    <Text textStyle="body" fontWeight="bold">
                        <Trans>Chord type</Trans>
                    </Text>
                </Box>
                <KeyQualityToggle value={keyQuality} setValue={setKeyQuality} />
            </Flex>
            <Flex
                minHeight="lg"
                alignItems="center"
                justifyContent="flex-end"
                borderTop="base"
                paddingX="lg"
            >
                <Button
                    variant="primary"
                    kind="minimal"
                    isRounded
                    onClick={handleApply}
                >
                    <Trans>Apply</Trans>
                </Button>
            </Flex>
        </Flex>
    );
};
